import React from 'react';
import { Link } from 'react-router-dom';
import { 
  GraduationCap, Users, CalendarDays, FileText, Coins, Bus, 
  BookOpen, Building, Briefcase, ArrowRight, ExternalLink, ChevronRight,
  Key, Receipt, CalendarClock, BookOpenCheck, FileEdit, PieChart, TrendingUp, 
  BookUser, Image, Utensils, MonitorPlay, FlaskConical, 
  Files, ScrollText, MessageSquare, Boxes, Award, Headphones
} from 'lucide-react';
import Education from '../../assets/Education.png'; // Assuming you have an image in assets

const CONTAINER = 'w-full max-w-7xl xl:max-w-[95%] 2xl:max-w-[85vw] mx-auto px-6 sm:px-8 lg:px-8';
const THEME_COLOR = '#3284c7';

/* ============================== PAGE DATA ============================== */

const FEATURES = [
  { title: 'Admission Management', icon: Users, desc: 'Streamline the entire student enrollment process from initial inquiry to final admission and document verification.' },
  { title: 'Manage Logins', icon: Key, desc: 'Securely control user access, roles, and permissions for students, teachers, and administrative staff.' },
  { title: 'Attendance', icon: CalendarDays, desc: 'Track student and staff presence with an intuitive administrative dashboard, utilizing a streamlined checklist layout for rapid marking.' },
  { title: 'Timetable', icon: CalendarClock, desc: 'Automate and manage class schedules, teacher assignments, and substitute tracking seamlessly.' },
  { title: 'Lesson Plan', icon: BookOpenCheck, desc: 'Empower teachers to structure daily lessons, set academic goals, and track syllabus completion.' },
  { title: 'Study Materials', icon: Files, desc: 'Upload and distribute digital resources, notes, and reference materials to students.' },
  { title: 'Syllabus', icon: ScrollText, desc: 'Define, update, and share course outlines and learning objectives clearly.' },
  { title: 'Homework', icon: FileEdit, desc: 'Assign, collect, and grade student homework digitally with automated notifications.' },
  { title: 'Examinations', icon: FileText, desc: 'Manage exam schedules, grading rubrics, automated result generation, and comprehensive student report cards effortlessly.' },
  { title: 'Online Classes', icon: MonitorPlay, desc: 'Seamlessly integrate virtual classrooms and remote learning tools for digital education.' },
  { title: 'Digital Labs', icon: FlaskConical, desc: 'Provide virtual lab environments and digital simulations for interactive STEM learning.' },
  { title: 'Fees Management', icon: Coins, desc: 'Automate fee collection, generate digital receipts, set up payment gateways, and track pending dues with automated reminders.' },
  { title: 'Daily Expenses', icon: Receipt, desc: 'Track daily operational expenses and maintain transparent financial records for the institution.' },
  { title: 'HR & Payroll', icon: Briefcase, desc: 'Manage complete staff records, payroll processing, leave allocations, and automated salary slip generation.' },
  { title: 'Performance', icon: TrendingUp, desc: 'Monitor and evaluate student and staff performance metrics over time.' },
  { title: 'Reports', icon: PieChart, desc: 'Generate comprehensive analytical reports for academic performance, attendance, and finances.' },
  { title: 'Transport', icon: Bus, desc: 'Manage vehicle routing, student stop allocations, driver details, and track transport fees in a centralized module.' },
  { title: 'Library', icon: BookOpen, desc: 'Complete digital library architecture. Features a responsive Subject Index and a textbook viewer where the textbook and keyword panes scroll independently while the chapters section remains static.' },
  { title: 'Hostel', icon: Building, desc: 'Allocate rooms efficiently, manage hostel fee structures, and track student whereabouts and visitor logs securely.' },
  { title: 'Food Menu', icon: Utensils, desc: 'Plan and publish cafeteria menus, track dietary preferences, and manage meal plans.' },
  { title: 'Inventory & Assets', icon: Boxes, desc: 'Track school equipment, lab apparatus, IT assets, and general inventory efficiently.' },
  { title: 'Directory', icon: BookUser, desc: 'Maintain a centralized contact directory for all students, parents, and staff members.' },
  { title: 'Gallery', icon: Image, desc: 'Share event photos, campus activities, and achievements in a secure media gallery.' },
  { title: 'PTM Schedule', icon: Users, desc: 'Organize Parent-Teacher Meetings efficiently with automated scheduling and reminders.' },
  { title: 'GroupChat', icon: MessageSquare, desc: 'Facilitate secure, moderated communication between students, teachers, and study groups.' },
  { title: 'Alumni', icon: Award, desc: 'Build and nurture an active alumni network, manage events, and track graduate success.' },
  { title: 'Support', icon: Headphones, desc: 'Provide a dedicated helpdesk for technical issues, campus maintenance, and student queries.' }
];

const EducationERP = () => {
  return (
    <div className="overflow-x-hidden pt-0">
      <style>{`
        @keyframes seFadeUp { from{opacity:0;transform:translateY(24px)} to{opacity:1;transform:translateY(0)} }
        .se-fade-up { animation: seFadeUp .7s ease-out both; }
        @keyframes seFloat { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-12px)} }
        @keyframes seFloatSlow { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-9px)} }
        .se-float { animation: seFloat 6s ease-in-out infinite; }
        .se-float-slow { animation: seFloatSlow 8s ease-in-out infinite; }
        @media (prefers-reduced-motion: reduce) { .se-float, .se-float-slow, .se-fade-up { animation: none !important; } }
      `}</style>

      {/* ================= HERO SECTION ================= */}
      <section className="relative overflow-hidden bg-gradient-to-b from-[#eef4fb] via-[#f5f9fd] to-white">
        <div className="pointer-events-none absolute -top-24 -right-24 w-96 h-96 rounded-full bg-primary/10 blur-3xl" />
        <div className="pointer-events-none absolute top-40 -left-24 w-80 h-80 rounded-full bg-[#f29132]/10 blur-3xl" />
        
        <div className={`${CONTAINER} relative py-5 lg:py-6`}>
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-8 items-center">
            
            <div className="max-w-2xl relative z-10 se-fade-up">
              <nav className="flex flex-wrap items-center gap-2 text-sm text-slate-500 mb-6">
                <Link to="/" className="hover:text-primary transition-colors">Home</Link>
                <ChevronRight className="w-3.5 h-3.5 text-slate-400" />
                <Link to="/products" className="hover:text-primary transition-colors">Products</Link>
                <ChevronRight className="w-3.5 h-3.5 text-slate-400" />
                <span className="text-primary font-medium">Education ERP</span>
              </nav>

              <span className="inline-flex items-center gap-2 text-xs font-bold tracking-[0.18em] uppercase text-primary bg-white ring-1 ring-primary/25 px-4 py-1.5 rounded-full mb-6">
                <GraduationCap className="w-4 h-4" /> SmartEdz Campus Solutions
              </span>
              <h1 className="text-4xl sm:text-5xl xl:text-[3.4rem] font-extrabold leading-[1.1] tracking-tight mb-6" style={{ color: '#13294b' }}>
                Comprehensive Education ERP for Modern Institutions
              </h1>
              <p className="text-lg text-slate-500 leading-relaxed mb-8">
                Transform your school, college, or university with a unified campus management system. Automate administrative tasks, enhance academic tracking, and improve communication across your entire institution.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link to="/request-demo" className="inline-flex items-center gap-2 rounded-lg px-7 py-3.5 font-semibold text-white shadow-lg shadow-primary/30 bg-primary hover:brightness-110 transition">
                  Request a Demo <ArrowRight className="w-4 h-4" />
                </Link>
                
                {/* Visit Website Button */}
                <a 
                  href="https://smartedzoriginalfrontend-production.up.railway.app/" 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="inline-flex items-center gap-2 rounded-lg px-7 py-3.5 font-semibold text-primary bg-white ring-1 ring-primary/40 hover:bg-primary/5 transition"
                >
                  Visit Website <ExternalLink className="w-4 h-4" />
                </a>
              </div>
            </div>

            {/* Replaced Mockups with Provided Image */}
            <div className="relative se-fade-up w-full" style={{ animationDelay: '.15s' }}>
              <div className="absolute inset-x-8 top-4 bottom-16 bg-gradient-to-tr from-primary/12 to-[#21BEE1]/12 blur-3xl rounded-[48px]" />
              <img 
                src={Education} 
                alt="Education ERP Dashboard Illustration" 
                className="relative z-10 w-full h-auto object-contain mix-blend-multiply hover:scale-[1.02] transition-transform duration-500"
              />
            </div>

          </div>
        </div>
      </section>

      {/* ================= CORE MODULES ================= */}
      <section className="bg-gradient-to-b from-[#f5f9fd] to-white py-14">
        <div className={CONTAINER}>
          <div className="text-center mb-10">
            <span className="block text-xs font-bold tracking-[0.2em] uppercase text-primary mb-3">Core Modules</span>
            <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight" style={{ color: '#13294b' }}>Everything you need in one dashboard</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {FEATURES.map((feature, index) => (
              <div key={index} className="group h-full flex flex-col bg-white rounded-2xl ring-1 ring-slate-100 p-6 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4 transition-transform group-hover:scale-110" style={{ backgroundColor: `${THEME_COLOR}1A` }}>
                  <feature.icon className="w-6 h-6" style={{ color: THEME_COLOR }} />
                </div>
                <h3 className="font-bold text-slate-800 text-lg mb-3">{feature.title}</h3>
                <p className="text-[13px] text-slate-500 leading-relaxed flex-1">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default EducationERP;